import ServiceFeeNote from "@/components/ServiceFeeNote";
import ShareMenu from "@/components/ShareMenu";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import { startCoinPayment } from "@/lib/coinpay";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill } from "@/components/perf";
import { toast } from "sonner";
import { Trophy, Calendar, MapPin, Users, DollarSign, Plus, ChevronRight, ChevronDown, X, Loader2, ClipboardList, Send, LayoutDashboard, Flag, Search, Info } from "lucide-react";
import TournamentCreateForm from "@/components/TournamentCreateForm";
import PartnerInvites from "@/components/PartnerInvites";
import PlayerName from "@/components/PlayerName";
import Avatar from "@/components/Avatar";
import PageBackdrop from "@/components/PageBackdrop";
import { TN_CSS } from "@/lib/tnButtons";
import AdBanner from "@/components/AdBanner";
import SponsorStrip from "@/components/SponsorStrip";
import { WaiverGate } from "@/components/TournamentWaiver";
import PhonePromptModal from "@/components/PhonePromptModal";
import SignupReceiptModal from "@/components/SignupReceiptModal";
import GenderGateModal from "@/components/GenderGateModal";

const FORMAT_LABELS = {
  single_elim: "Single Elimination", double_elim: "Double Elimination",
  round_robin: "Round Robin", pool_play: "Pool Play → Playoffs",
};
import { DIVISION_RANGES, DIVISION_ORDER, ALL_DIVISIONS, divisionRange } from "@/lib/divisions";
const DIVISIONS = ["Low Intermediate", "Intermediate", "High Intermediate", "Advanced", "Future Pro", "Pro"];
const ELIG_GATES = {
  regional: "PSI 62+",
  state: "PSI 70+ · 3 tournaments played",
  national: "PSI 78+ · 4 tournaments · podium finish",
};

// Men's / Women's events only show to players whose profile gender matches (Mixed & Coed are open to all)
export const eventNeedsGender = (et) => {
  const el = (et || "").toLowerCase();
  if (el.includes("mixed") || el.includes("coed")) return null;
  if (el.includes("women") || el.includes("ladies") || el.includes("girls")) return "female";
  if (el.includes("men") || el.includes("boys")) return "male";
  return null;
};
export const eventFitsGender = (et, gender) => {
  const need = eventNeedsGender(et);
  const g = (gender || "").toLowerCase();
  return !(need && g && g !== need);
};
const priceLabel = (t) => {
  if (t.price_display === "coin" && t.entry_fee_coins) return `🪙 ${t.entry_fee_coins} BLC entry`;
  const fee = t.effective_entry_fee ?? t.entry_fee;
  return fee > 0 ? `$${fee} entry` : "Free entry";
};

const divOptions = (t, events) => {
  const cfg = t?.event_config || {};
  const evs = (events || []).filter((e) => cfg[e]);
  if (evs.length) {
    // divisions offered in every selected event; fall back to the union when they don't overlap
    const lists = evs.map((e) => (cfg[e].levels || []).includes("All") ? DIVISIONS : cfg[e].levels || []);
    const inter = lists.reduce((a, b) => a.filter((x) => b.includes(x)));
    const pool = inter.length ? inter : [...new Set(lists.flat())];
    return DIVISIONS.filter((d) => pool.includes(d));
  }
  const lv = (t?.levels || []).filter((l) => l !== "All");
  return lv.length ? lv : DIVISIONS;
};
const ageOptions = (t, events) => {
  const cfg = t?.event_config || {};
  const evs = (events || []).filter((e) => cfg[e]);
  if (evs.length) return [...new Set(evs.flatMap((e) => cfg[e].age_brackets || []))];
  return t?.age_brackets || [];
};

export default function Tournaments() {
  const { user, realUser } = useAuth();
  const navigate = useNavigate();
  const [tourneys, setTourneys] = useState([]);
  const [registering, setRegistering] = useState(null);
  const [waiverOk, setWaiverOk] = useState(false);
  const [phoneAsk, setPhoneAsk] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [genderGate, setGenderGate] = useState(null); // tournament waiting for the gender choice
  const openRegisterAfterGender = useCallback((t) => {
    setPartners({}); setRegVip(false); setRegEvents(t.event_types?.length === 1 ? [t.event_types[0]] : []);
    setRegAge(t.age_brackets?.length === 1 ? t.age_brackets[0] : ""); const dv = divOptions(t); setRegDivision(dv.length === 1 ? dv[0] : "");
    setRegistering(t); setPhoneAsk(!user?.phone);
  }, [user]); // eslint-disable-line
  const openRegister = useCallback((t) => {
    if (!user?.gender) { setGenderGate(t); return; } // big gate first — no particulars until gender is on file
    openRegisterAfterGender(t);
  }, [user, openRegisterAfterGender]);
  const [partners, setPartners] = useState({});
  const [regEvents, setRegEvents] = useState([]);
  const [regAge, setRegAge] = useState("");
  const [regDivision, setRegDivision] = useState("");
  const [regVip, setRegVip] = useState(false);
  const [search, setSearch] = useState("");
  const [nearMe, setNearMe] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [params, setParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(async () => {
    const { data: t } = await api.get("/tournaments");
    setTourneys(t);
  }, []);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setVerifying(false); toast.error("Payment status check timed out. Check back shortly."); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setVerifying(false); load();
        if (data.kind === "tournament_entry") setReceipt({ amount: (data.amount_total || 0) / 100, name: data.name, conf: sessionId.slice(-8).toUpperCase() });
        else toast.success("Payment successful — you're in the tournament!");
        return;
      }
      if (data.status === "expired") { setVerifying(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch { setVerifying(false); toast.error("Could not verify payment."); }
  }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) { setVerifying(true); pollStatus(sid); params.delete("session_id"); setParams(params, { replace: true }); }
  }, []); // eslint-disable-line

  useEffect(() => {
    const rid = params.get("register");
    if (!rid || !tourneys.length || registering) return;
    const t = tourneys.find((x) => x.id === rid);
    params.delete("register"); params.delete("from"); setParams(params, { replace: true });
    if (t) { if (t.my_events?.length) toast("You're already registered for this tournament"); else openRegister(t); }
    else toast.error("That tournament is no longer open for registration");
  }, [tourneys]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);


  const register = async () => {
    const hasEvents = (registering.event_types || []).length > 0;
    if (hasEvents && regEvents.length === 0) {
      toast.error("Pick at least one event");
      return;
    }
    if (!waiverOk) { toast.error("Please read and check the Tournament Liability Waiver first"); return; }
    setBusy(true);
    try {
      const payloadEvents = hasEvents
        ? regEvents.map((et) => ({ event_type: et, age_bracket: regAge || null, division: regDivision || null, partner_id: partners[et]?.id || null }))
        : [{ event_type: null, age_bracket: regAge || null, division: regDivision || null, partner_id: null }];
      const { data } = await api.post(`/tournaments/${registering.id}/register`, {
        events: payloadEvents, origin_url: window.location.origin, vip: regVip,
      });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      if (data.waitlisted) toast.success(`Event is full — you're #${data.position} on the waiting list. We'll notify you when a spot opens!`);
      else toast.success(data.perk_used || (data.count > 1 ? `You're registered for ${data.count} events!` : "You're registered!"));
      setRegistering(null); setPartners({}); setRegEvents([]); setRegAge(""); setRegDivision(""); setRegVip(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const input = "w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red text-sm";
  const lbl = "block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5";
  const q = search.trim().toLowerCase();
  const matchesSearch = (t) => !q || [t.name, t.location, t.venue, t.format, t.pbr_level, t.director_name,
    ...(t.levels || []), ...(t.event_types || []), ...(t.age_brackets || [])].filter(Boolean).join(" ").toLowerCase().includes(q);
  const withinRadius = (t) => !nearMe || t.distance_miles == null || t.distance_miles <= 100;
  const upcoming = tourneys.filter((t) => t.status !== "complete" && matchesSearch(t) && withinRadius(t))
    .sort((a, b) => (a.date || "9999").localeCompare(b.date || "9999"));
  const past = tourneys.filter((t) => t.status === "complete" && matchesSearch(t));
  const railCards = showAll ? upcoming : upcoming.slice(0, 8);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="tournaments-page">
      <style>{TN_CSS}</style>
      <PageBackdrop ck="backdrop.tournaments" image="https://static.prod-images.emergentagent.com/jobs/ccb7baf7-4fe3-4351-b3c9-d8e5a28ac676/images/0e732a824e17bfe7511612a636a39799f88ef4a50cbf04284258831c063c5c56.png" />
      <div className="relative z-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4 sm:gap-6 w-full sm:w-auto">
          <img src="/icons/pbpa-tour.png" alt="PBPA Tour Series" data-testid="tournaments-hub-logo"
            className="w-24 h-24 sm:w-36 sm:h-36 lg:w-44 lg:h-44 object-contain shrink-0 drop-shadow-[0_0_18px_rgba(163,230,53,0.45)] animate-fade-up" />
          <PageHeader kicker={user?.is_td ? "Director Console · Your Events" : "Compete · PBPA Tour Series"}
          title="Tournament Hub"
          subtitle={user?.is_td
            ? "Create and run your tournaments — brackets, courts, score approvals and champions. You only see events you direct."
            : "Browse upcoming tournaments, register for your events and follow live brackets."} />
        </div>
        <AdBanner zone="tournaments" />
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="champions-wall-link" onClick={() => navigate("/champions")}
            className="tn-btn tn-champ h-[58px] w-[230px] active:scale-95 transition-transform">
            <span className="tn-inner text-[10px] tracking-[0.2em]"><Trophy className="w-4 h-4" /> Wall of Champions</span>
          </button>
          <button data-testid="tour-standings-link" onClick={() => navigate("/tour")}
            className="tn-btn tn-champ h-[58px] w-[230px] active:scale-95 transition-transform">
            <span className="tn-inner text-[10px] tracking-[0.2em]"><Flag className="w-4 h-4" /> Tour Standings</span>
          </button>
          <ShareMenu url={`${process.env.REACT_APP_BACKEND_URL}/api/share/tournaments`} text="PBPA Tournaments — Play. Compete. Connect."
            image="/brand/pbpa-tournaments-share.jpg" testId="share-tournament-hub" label="Share Hub" />
          <button data-testid="player-hub-link" onClick={() => navigate("/tournaments/hub")}
            className="tn-btn tn-hub h-[68px] w-[250px] active:scale-95 transition-transform">
            <span className="tn-inner text-sm tracking-[0.24em]"><LayoutDashboard className="w-5 h-5" /> Player Hub</span>
          </button>
        </div>
      </div>

      {verifying && (
        <div className="flex items-center gap-2 text-pb-cyan text-sm mb-6" data-testid="payment-verifying">
          <Loader2 className="w-4 h-4 animate-spin" /> Verifying your entry payment…
        </div>
      )}

      <PartnerInvites />

      <h3 className="font-display font-bold text-xl uppercase mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-pb-red" /> Upcoming & Live</h3>

      <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="tournament-search-bar">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input data-testid="tournament-search-input" value={search} onChange={(e) => { setSearch(e.target.value); setShowAll(false); }}
            placeholder="Search tournaments — name, city, venue, division, event type…"
            className="w-full bg-pb-elevated border border-pb-border pl-9 pr-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan text-sm" />
        </div>
        <button data-testid="near-me-toggle" onClick={() => user?.zip_code ? setNearMe(!nearMe) : toast.info("Add your home zip code on your Player Profile to find tournaments near you")}
          className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3.5 py-2.5 border transition-colors ${
            nearMe ? "bg-pb-cyan text-black border-pb-cyan" : "border-pb-border text-zinc-400 hover:border-pb-cyan hover:text-pb-cyan"}`}>
          <MapPin className="w-3.5 h-3.5" /> Within 100 mi{user?.zip_code ? ` of ${user.zip_code}` : ""}
        </button>
      </div>

      {upcoming.length === 0 && (
        <div className="text-sm text-zinc-600 border border-pb-border bg-pb-surface p-5 mb-10" data-testid="no-tournaments">
          {q || nearMe ? "No tournaments match your search — try clearing the search or the 100-mile filter." : "No tournaments posted yet — check back soon."}
        </div>
      )}

      {!showAll ? (
        <>
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory mb-2 -mx-1 px-1" data-testid="tournament-rail">
            {railCards.map((t) => (
              <div key={t.id} className="w-[330px] min-w-[330px] snap-start shrink-0">
                <TournamentCard t={t} onChanged={load} onView={() => navigate(`/tournaments/${t.id}`)}
                  onRegister={() => openRegister(t)} />
              </div>
            ))}
          </div>
          {upcoming.length > 8 && (
            <button data-testid="show-more-tournaments" onClick={() => setShowAll(true)}
              className="w-full border border-pb-border text-zinc-300 font-bold uppercase tracking-widest text-xs py-3 mb-10 hover:border-pb-cyan hover:text-pb-cyan transition-colors">
              Show All {upcoming.length} Tournaments <ChevronDown className="w-4 h-4 inline" />
            </button>
          )}
          {upcoming.length > 0 && upcoming.length <= 8 && <div className="mb-10" />}
        </>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4" data-testid="tournament-gallery">
            {upcoming.map((t) => (
              <TournamentCard key={t.id} t={t} onChanged={load} onView={() => navigate(`/tournaments/${t.id}`)}
                onRegister={() => openRegister(t)} />
            ))}
          </div>
          <button data-testid="show-less-tournaments" onClick={() => setShowAll(false)}
            className="w-full border border-pb-border text-zinc-500 font-bold uppercase tracking-widest text-xs py-3 mb-10 hover:border-pb-cyan hover:text-pb-cyan transition-colors">
            Show Less
          </button>
        </>
      )}

      {past.length > 0 && (
        <>
          <h3 className="font-display font-bold text-xl uppercase mb-4 text-zinc-400">Completed</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12 opacity-80">
            {past.map((t) => <TournamentCard key={t.id} t={t} onView={() => navigate(`/tournaments/${t.id}`)} />)}
          </div>
        </>
      )}

      {user?.is_td && (
        <div className="border border-pb-border bg-pb-surface p-5 mb-12 flex flex-wrap items-center justify-between gap-4" data-testid="create-tournament-footer">
          <div>
            <div className="font-display font-bold text-lg uppercase">Run Your Own Event</div>
            <div className="text-sm text-zinc-500">Set up brackets, courts, entry fees and divisions in minutes.</div>
          </div>
          <button data-testid="create-tournament-btn" onClick={() => setShowCreate(true)}
            className="tn-btn tn-create h-[60px] w-[260px] active:scale-95 transition-transform">
            <span className="tn-inner text-[11px] tracking-[0.2em]"><Plus className="w-4 h-4" /> Create Tournament</span>
          </button>
        </div>
      )}

      {!realUser?.is_td && <TdApplyCard />}

      <GenderGateModal open={!!genderGate} onCancel={() => setGenderGate(null)}
        onDone={() => { const t = genderGate; setGenderGate(null); setTimeout(() => openRegisterAfterGender(t), 0); }} />

      {registering && (() => {
        const regFull = registering.entry_count >= registering.capacity;
        const isAdd = (registering.my_events || []).length > 0;
        const maxEv = regFull ? 1 : 3;
        const baseFee = registering.effective_entry_fee ?? registering.entry_fee;
        const firstFee = isAdd && registering.additional_event_fee != null ? registering.additional_event_fee : baseFee;
        const addlFeeEach = registering.additional_event_fee != null ? registering.additional_event_fee : registering.entry_fee;
        const nEv = Math.max(regEvents.length, 1);
        const vipFee = registering.vip_addon_fee != null ? Number(registering.vip_addon_fee) : null;
        const vipAmt = regVip && vipFee ? vipFee : 0;
        const effFee = Math.round((firstFee + addlFeeEach * (nEv - 1) + vipAmt) * 100) / 100;
        const effCoins = isAdd && registering.additional_event_fee_coins != null ? registering.additional_event_fee_coins : registering.entry_fee_coins;
        const earlyBird = !isAdd && registering.early_bird_active;
        const toggleEvent = (et) => setRegEvents((prev) => prev.includes(et)
          ? prev.filter((x) => x !== et)
          : prev.length >= maxEv ? prev : [...prev, et]);
        const doublesEvents = regEvents.filter((et) => et.toLowerCase().includes("doubles"));
        return (
        <Modal onClose={() => { setRegistering(null); setRegEvents([]); setPartners({}); setWaiverOk(false); }} testid="register-modal" title={`${regFull ? "Join Waiting List" : "Register"} — ${registering.name}`}>
          <div className="space-y-4">
            <div className="text-sm text-zinc-400">{FORMAT_LABELS[registering.format]} · {registering.date}{registering.end_date ? ` – ${registering.end_date}` : ""} · {registering.location}</div>
            {regFull && (
              <div className="border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-300" data-testid="waitlist-notice">
                This event is full. You'll join the waiting list (no charge now) — if a spot opens up, the director promotes you in order and you'll get an instant notification.
              </div>
            )}
            {(registering.event_types || []).length > 0 && (
              <div>
                <label className={lbl}>Events * <span className="text-zinc-600 normal-case tracking-normal">(pick up to {maxEv})</span></label>
                <div className="space-y-1.5" data-testid="register-event-list">
                  {registering.event_types.filter((et) => eventFitsGender(et, user?.gender)).map((et) => {
                    const entered = (registering.my_events || []).includes(et);
                    const genderUnknown = eventNeedsGender(et) && !user?.gender;
                    const checked = regEvents.includes(et);
                    const disabled = (entered && !(registering.age_brackets || []).length) || genderUnknown || (!checked && regEvents.length >= maxEv);
                    return (
                      <label key={et} data-testid={`register-event-option-${et}`}
                        className={`flex items-center gap-2.5 border px-3 py-2 cursor-pointer transition-colors ${checked ? "border-pb-red bg-pb-red/5" : "border-pb-border bg-pb-elevated"} ${disabled ? "opacity-40 cursor-not-allowed" : "hover:border-pb-red/60"}`}>
                        <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleEvent(et)}
                          className="accent-[#E10600] w-4 h-4" />
                        <span className="text-sm font-semibold flex-1">{et}</span>
                        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
                          {entered ? "already entered" : genderUnknown ? "set gender in Profile" : ""}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {!user?.gender && registering.event_types.some((et) => eventNeedsGender(et)) && (
                  <div className="text-[10px] text-amber-400 mt-1" data-testid="register-gender-hint">
                    Gendered events are locked until you set your gender on your Profile.
                  </div>
                )}
                {(registering.my_events || []).length > 0 && (
                  <div className="text-[10px] text-zinc-500 mt-1" data-testid="register-multi-note">
                    You're in {registering.my_events.join(" + ")} — pick another event to play multiple divisions. Each event is a separate entry{registering.entry_fee > 0 ? " and entry fee" : ""}.
                  </div>
                )}
              </div>
            )}
            {ageOptions(registering, regEvents).length > 0 && (
              <div>
                <label className={lbl}>Age Group *</label>
                <select data-testid="register-age-bracket" value={regAge} onChange={(e) => setRegAge(e.target.value)} className={input}>
                  <option value="">Choose your age group…</option>
                  {ageOptions(registering, regEvents).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className={lbl}>Division *</label>
              <select data-testid="register-division" value={regDivision} onChange={(e) => setRegDivision(e.target.value)} className={input}>
                <option value="">Choose your division…</option>
                {(() => {
                  const mine = DIVISION_ORDER[user?.division];
                  return divOptions(registering, regEvents).map((d) => {
                    const below = mine != null && DIVISION_ORDER[d] != null && DIVISION_ORDER[d] < mine;
                    const rng = DIVISION_RANGES[d] ? ` (${DIVISION_RANGES[d]})` : "";
                    return <option key={d} value={d} disabled={below}>{d}{rng}{below ? ` — below your ${user.division} class` : ""}</option>;
                  });
                })()}
              </select>
              {user?.division && <div className="text-[10px] text-zinc-500 mt-1" data-testid="register-class-note">Your PSI classification: <span className="text-pb-cyan font-bold">{user.division}</span> — you can enter your class or higher. Directors can place you lower manually.</div>}
            </div>
            {doublesEvents.map((et) => (
              <PartnerSearch key={et} label={`Partner — ${et} ${registering.partner_required ? "*" : ""}`}
                optional={!registering.partner_required} partner={partners[et] || null}
                onSelect={(u2) => setPartners((p) => ({ ...p, [et]: u2 }))}
                onClear={() => setPartners((p) => { const n2 = { ...p }; delete n2[et]; return n2; })}
                fee={registering.entry_fee} input={input} lbl={lbl} />
            ))}
            {vipFee != null && !regFull && (
              <label data-testid="register-vip-toggle"
                className={`flex items-center gap-2.5 border px-3 py-2.5 cursor-pointer transition-colors ${regVip ? "border-yellow-400 bg-yellow-400/10" : "border-pb-border bg-pb-elevated hover:border-yellow-400/60"}`}>
                <input type="checkbox" checked={regVip} onChange={() => setRegVip((v) => !v)} className="accent-[#FACC15] w-4 h-4" data-testid="register-vip-checkbox" />
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-yellow-300">Add VIP Section · ${vipFee}{registering.vip_addon_fee_coins ? ` / 🪙 ${registering.vip_addon_fee_coins} BLC` : ""}</span>
                  <span className="block text-[10px] text-zinc-500">Optional upgrade — added to your total, flagged VIP on the roster.</span>
                </span>
              </label>
            )}
            {earlyBird && !regFull && (
              <div className="border border-emerald-500/40 bg-emerald-500/5 p-2.5 text-[11px] text-emerald-300 flex items-center gap-2" data-testid="register-early-bird-note">
                🐦 Early Bird price <span className="font-bold">${registering.early_bird_fee}</span> <span className="text-zinc-500 line-through">${registering.entry_fee}</span>
                {registering.early_bird_expires_at && <span className="text-zinc-500">· ends {new Date(registering.early_bird_expires_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>}
              </div>
            )}
            <WaiverGate tid={registering.id} eventName={registering.name} onChange={setWaiverOk} testid="register-waiver" />
            {effFee > 0 && !regFull && (
              <div className="border border-pb-red/30 bg-pb-red/5 p-3 text-sm flex items-center gap-2 flex-wrap">
                <DollarSign className="w-4 h-4 text-pb-red" /> Entry fee: <span className="font-bold text-pb-red">${effFee}</span>
                {vipAmt > 0 && <span className="text-[10px] text-yellow-300 uppercase tracking-wider font-bold" data-testid="vip-fee-note">incl. VIP Section ${vipAmt}</span>}
                {nEv > 1 && (
                  <span className="text-[10px] text-emerald-300 uppercase tracking-wider font-bold" data-testid="multi-fee-breakdown">
                    {nEv} events — ${firstFee} + {nEv - 1} × ${addlFeeEach}
                  </span>
                )}
                {nEv === 1 && isAdd && registering.additional_event_fee != null && firstFee !== registering.entry_fee && (
                  <span className="text-[10px] text-emerald-300 uppercase tracking-wider font-bold" data-testid="additional-fee-note">2nd-event price · reg. ${registering.entry_fee}</span>
                )} — paid securely via Stripe
              </div>
            )}
            <button data-testid="register-submit" onClick={register} disabled={busy || !waiverOk}
              title={waiverOk ? undefined : "Check the waiver box first"}
              className={`w-full active:scale-95 transition-all disabled:opacity-50 tn-btn h-[54px] ${regFull ? "tn-wait" : "tn-reg"}`}>
              <span className="tn-inner text-[11px] tracking-[0.2em]">
                {busy ? "…" : !waiverOk ? "Sign the waiver to continue" : regFull ? "Join Waiting List — Free" : effFee > 0 ? `Pay $${effFee} & Register` : "Register Free"}
              </span>
            </button>
            {effFee > 0 && !regFull && <ServiceFeeNote className="text-center" kind="tournament" />}
            {effCoins && !regFull && (() => {
              const firstCoins = isAdd && registering.additional_event_fee_coins != null ? registering.additional_event_fee_coins : registering.entry_fee_coins;
              const addlCoinsEach = registering.additional_event_fee_coins != null ? registering.additional_event_fee_coins : registering.entry_fee_coins;
              const vipCoins = regVip && registering.vip_addon_fee_coins ? Number(registering.vip_addon_fee_coins) : 0;
              const totalCoins = Math.round((firstCoins + addlCoinsEach * (nEv - 1) + vipCoins) * 100) / 100;
              if (regVip && !registering.vip_addon_fee_coins) return (
                <div className="text-[10px] text-zinc-500 text-center" data-testid="register-coin-vip-unavailable">VIP Section is card-only at this event — uncheck VIP to pay with Coin.</div>
              );
              return (
              <button data-testid="register-coin-btn" disabled={busy || !waiverOk}
                onClick={() => {
                  const hasEvents = (registering.event_types || []).length > 0;
                  if (hasEvents && regEvents.length === 0) { toast.error("Pick at least one event"); return; }
                  if (!waiverOk) { toast.error("Please read and check the Tournament Liability Waiver first"); return; }
                  const payload = regEvents.length > 1
                    ? { events: regEvents.map((et) => ({ event_type: et, age_bracket: regAge || null, division: regDivision || null, partner_id: partners[et]?.id || null })), origin_url: window.location.origin, vip: regVip }
                    : { event_type: regEvents[0] || null, age_bracket: regAge || null, division: regDivision || null, partner_id: (partners[regEvents[0]] || {}).id || null, origin_url: window.location.origin, vip: regVip };
                  startCoinPayment(navigate, { kind: "tournament_entry", item_id: registering.id, payload });
                }}
                className="w-full border border-purple-400/60 text-purple-300 hover:bg-purple-400/10 font-bold uppercase tracking-widest py-2.5 text-xs active:scale-95 transition-all disabled:opacity-50">
                🪙 Pay with Coin · {totalCoins} BLC{nEv > 1 ? ` (${nEv} events)` : ""}
              </button>
              );
            })()}
          </div>
        </Modal>
        );
      })()}

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} testid="create-modal" title="Create Tournament" wide>
          <TournamentCreateForm onCreated={(data) => { setShowCreate(false); navigate(`/tournaments/${data.id}`); }} />
        </Modal>
      )}
      </div>
    </div>
  );
}

function PartnerSearch({ label, optional, partner, onSelect, onClear, fee, input, lbl }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const tRef = useRef();
  const onChange = (v) => {
    setQ(v);
    clearTimeout(tRef.current);
    if (v.trim().length < 2) { setResults([]); return; }
    tRef.current = setTimeout(async () => {
      try { const { data } = await api.get(`/members/search?q=${encodeURIComponent(v.trim())}`); setResults(data); }
      catch { setResults([]); }
    }, 300);
  };
  return (
    <div className="relative">
      <label className={lbl}>{label} {optional && <span className="text-zinc-600 normal-case tracking-normal">(optional — add later if you don't have one yet)</span>}</label>
      {partner ? (
        <div className="flex items-center gap-3 bg-pb-elevated border border-pb-cyan/40 p-2.5" data-testid="selected-partner">
          <Avatar src={partner.avatar} name={partner.name} size={32} />
          <span className="flex-1 text-sm font-semibold truncate">{partner.name}</span>
          <button data-testid="clear-partner" onClick={() => { onClear(); setQ(""); }}
            className="text-zinc-500 hover:text-pb-red"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <>
          <input data-testid="register-partner" value={q} onChange={(e) => onChange(e.target.value)}
            placeholder="Search members — partners must be Bullpen members" className={input} autoComplete="off" />
          {results.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-pb-surface border border-pb-cyan/50 divide-y divide-pb-border/50 max-h-52 overflow-y-auto shadow-xl" data-testid="partner-results">
              {results.map((u) => (
                <button key={u.id} type="button" data-testid={`partner-result-${u.id}`}
                  onClick={() => { onSelect(u); setQ(""); setResults([]); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-pb-elevated transition-colors">
                  <Avatar src={u.avatar} name={u.name} size={28} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold truncate">{u.name}</span>
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500">{u.membership} member{u.location ? ` · ${u.location}` : ""}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
      {partner && fee > 0 && (
        <p className="text-[11px] text-amber-400/90 mt-1.5" data-testid="partner-pay-note">
          {partner.name} pays their own ${fee} entry — they'll get an instant notification + email
          with a payment link and show as PENDING until they pay.
        </p>
      )}
    </div>
  );
}

function EditMyReg({ t, onChanged }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [idx, setIdx] = useState(0);
  const [f, setF] = useState({});
  const regs = t.my_regs || [];
  if (!regs.length || t.status !== "open" || (t.reg_state && t.reg_state !== "open")) return null;
  const sel = "bg-pb-elevated border border-pb-border px-2 py-1.5 text-[11px] text-white focus:outline-none max-w-full";
  const pick = (i) => { setIdx(i); setF({ event_type: regs[i].event_type || "", division: regs[i].division || "", age_bracket: regs[i].age_bracket || "" }); };
  if (!open) return (
    <button data-testid={`edit-my-reg-btn-${t.id}`} onClick={() => { pick(0); setOpen(true); }}
      className="flex items-center justify-center gap-1 border border-pb-border text-zinc-400 font-bold uppercase tracking-widest text-[10px] py-2 hover:border-pb-cyan hover:text-pb-cyan transition-colors">
      ✏️ Edit My Registration
    </button>
  );
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/tournaments/${t.id}/my-registration`, {
        current_event: regs[idx].event_type, event_type: f.event_type || null,
        division: f.division || null, age_bracket: f.age_bracket ?? null,
      });
      toast.success("Registration updated — your partner was notified");
      setOpen(false); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="border border-pb-cyan/30 bg-pb-cyan/5 p-2.5 flex flex-wrap items-center gap-1.5" data-testid={`edit-my-reg-panel-${t.id}`}>
      {regs.length > 1 && (
        <select data-testid="myreg-pick-entry" value={idx} onChange={(e) => pick(Number(e.target.value))} className={sel}>
          {regs.map((r, i) => <option key={i} value={i}>{r.event_type}</option>)}
        </select>
      )}
      {(t.event_types || []).length > 1 && (
        <select data-testid="myreg-event" value={f.event_type} onChange={(e) => {
          const ev = e.target.value;
          const dv = divOptions(t, [ev]);
          const ab = ageOptions(t, [ev]);
          setF({ ...f, event_type: ev, division: dv.includes(f.division) ? f.division : (dv.length === 1 ? dv[0] : ""), age_bracket: ab.includes(f.age_bracket) ? f.age_bracket : (ab.length === 1 ? ab[0] : "") });
        }} className={sel}>
          {(t.event_types || []).filter((x) => x === f.event_type || eventFitsGender(x, user?.gender)).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      )}
      <select data-testid="myreg-division" value={f.division} onChange={(e) => setF({ ...f, division: e.target.value })} className={sel}>
        {!divOptions(t, [f.event_type]).includes(f.division) && <option value="">Choose division…</option>}
        {divOptions(t, [f.event_type]).map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      {ageOptions(t, [f.event_type]).length > 0 && (
        <select data-testid="myreg-age" value={f.age_bracket} onChange={(e) => setF({ ...f, age_bracket: e.target.value })} className={sel}>
          <option value="">Choose age group…</option>
          {ageOptions(t, [f.event_type]).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      )}
      <button data-testid="myreg-save" disabled={busy} onClick={save}
        className="bg-pb-cyan text-black text-[10px] font-black uppercase tracking-widest px-3 py-1.5 hover:opacity-90 disabled:opacity-50">Save</button>
      <button data-testid="myreg-cancel" onClick={() => setOpen(false)} className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">Cancel</button>
      <span className="w-full text-[9px] text-zinc-600">Moves your whole team. Locks once brackets are generated — after that, ask the TD.</span>
    </div>
  );
}

function TournamentCard({ t, onView, onRegister, onChanged }) {
  const { user } = useAuth();
  const [preview, setPreview] = useState(false);
  const [roster, setRoster] = useState(null);
  const [openLevel, setOpenLevel] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [showSched, setShowSched] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const toggleResults = () => {
    const next = !showResults;
    setShowResults(next);
    if (next && results === null) {
      api.get(`/pbr/tournament/${t.id}/leaderboard`).then(({ data }) => setResults(data.rows)).catch(() => setResults([]));
    }
  };
  const sched = t.published_schedule || [];
  const schedDays = [...new Set(sched.map((s) => s.day || 1))].sort((a, b) => a - b);
  const dayDate = (day) => {
    if (!t.date) return null;
    const d = new Date(`${t.date}T00:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + (day - 1));
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  };
  const toggleGroup = (ev) => {
    const opening = !openGroups[ev];
    setOpenGroups((g) => ({ ...g, [ev]: opening }));
    if (!opening && openLevel?.startsWith(`${ev}|`)) setOpenLevel(null);
  };
  const canSeeBracket = user?.role === "admin" || user?.is_td;
  const spotsLeft = t.capacity - t.entry_count;
  const toggleLevel = async (lv) => {
    if (openLevel === lv) { setOpenLevel(null); return; }
    setOpenLevel(lv);
  };
  useEffect(() => {
    if (t.entry_count > 0 && t.levels?.length && !roster) {
      api.get(`/tournaments/${t.id}/roster-by-level`).then(({ data }) => setRoster(data)).catch(() => setRoster({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t.id, t.entry_count]);
  const imgSrc = t.image ? (t.image.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + t.image : t.image) : null;
  const vidSrc = t.video ? (t.video.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + t.video : t.video) : null;
  const heroSrc = imgSrc || t.logo || null;
  return (
    <div data-testid={`tournament-card-${t.id}`} className="bg-pb-surface border border-pb-border p-5 flex flex-col overflow-hidden hover:border-pb-red/50 transition-colors">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div className="font-display font-black text-xl uppercase leading-tight min-w-0 break-words">{t.name}</div>
        <div className="flex flex-wrap items-center justify-end gap-1.5 min-w-0">
          {t.status === "open" && t.reg_state === "opening_soon" && (
            <span data-testid={`reg-soon-${t.id}`}
              className="text-[9px] font-black uppercase tracking-widest px-2 py-1 border border-yellow-500/70 text-yellow-400 bg-yellow-500/10 animate-pulse">
              Opening Soon{t.reg_open_at ? ` · ${new Date(t.reg_open_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}
            </span>
          )}
          {t.status === "open" && t.reg_state === "closed" && (
            <span data-testid={`reg-closed-${t.id}`}
              className="text-[9px] font-black uppercase tracking-widest px-2 py-1 border border-zinc-600 text-zinc-400 bg-zinc-800/40">
              Registration Closed
            </span>
          )}
          {t.status === "open" && t.reg_state === "open" && t.reg_close_at && (
            <span data-testid={`reg-closes-${t.id}`}
              className="text-[9px] font-black uppercase tracking-widest px-2 py-1 border border-pb-cyan/50 text-pb-cyan bg-pb-cyan/5">
              Reg closes {new Date(t.reg_close_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </span>
          )}
          {t.status === "open" && (
            <span data-testid={`spots-left-${t.id}`}
              className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 border ${
                spotsLeft <= 0 ? "border-amber-500/70 text-amber-400 bg-amber-500/10"
                : spotsLeft < 3 ? "border-red-400/80 text-red-300 bg-red-500/10 tn-spots-low"
                : "border-emerald-500/50 text-emerald-300 bg-emerald-500/5"}`}>
              {spotsLeft <= 0 ? "Full" : `${spotsLeft} spot${spotsLeft === 1 ? "" : "s"} left`}
            </span>
          )}
          <Pill color={t.status === "complete" ? "#71717A" : t.status === "in_progress" ? "#22D3EE" : "#A3E635"}>
            {t.status === "in_progress" ? "Live" : t.status === "complete" ? "Final" : "Open"}
          </Pill>
          <ShareMenu url={`${process.env.REACT_APP_BACKEND_URL}/api/share/events/${t.id}`}
            text={`${t.name} — ${t.date}${t.location ? ` @ ${t.location}` : ""}. Register on Pickle Bullpen!`}
            image={`${process.env.REACT_APP_BACKEND_URL}/api/share/events/${t.id}/card.png`}
            testId={`share-tournament-${t.id}`} />
          {t.status === "open" && (
            <button data-testid={`copy-signup-link-${t.id}`} title="Copy public sign-up link (no account needed)"
              onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(`${window.location.origin}/toursignup?t=${t.id}`); toast.success("Public sign-up link copied — anyone can register from it, no account needed"); }}
              className="text-[9px] font-black uppercase tracking-widest border border-pb-border text-zinc-400 px-2 py-1 hover:border-pb-cyan hover:text-pb-cyan transition-colors">Sign-up link</button>
          )}
        </div>
      </div>
      {(vidSrc || heroSrc) && (
        <div className="relative w-full h-[200px] mb-3 border border-pb-border bg-black overflow-hidden group cursor-pointer" onClick={() => setPreview(true)}
          title="Tap to view full announcement & event details" data-testid={`card-media-${t.id}`}>
          {vidSrc ? (
            <video src={vidSrc} autoPlay muted loop playsInline className="w-full h-full object-contain group-hover:opacity-90 transition-opacity" data-testid={`card-video-${t.id}`} />
          ) : (
            <img src={heroSrc} alt="" className="w-full h-full object-contain group-hover:opacity-90 transition-opacity" data-testid={`card-image-${t.id}`} />
          )}
          <button type="button" onClick={(e) => { e.stopPropagation(); setPreview(true); }} data-testid={`card-info-btn-${t.id}`}
            className="tn-info-glow absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 text-[9px] font-black uppercase tracking-widest bg-black/70 backdrop-blur border border-pb-red/60 text-pb-red hover:bg-pb-red hover:text-black transition-colors">
            <Info className="w-3 h-3" /> Tournament info
          </button>
        </div>
      )}
      <div className="text-xs text-zinc-400 space-y-1.5 flex-1">
        <div className="flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-pb-red" /> {FORMAT_LABELS[t.format]}{t.play_style ? ` · ${t.play_style === "come_around" ? "Come Around" : "No Come Around"}` : ""}</div>
        {!(t.levels?.length && t.event_types?.length) && <div className="text-zinc-500">{(t.event_types || []).join(" · ")}</div>}
        {t.levels?.length > 0 && (() => {
          const inEv = (ev) => (e) => !ev || !e.event_type || e.event_type === ev;
          // per-event classifications × age groups when the TD configured them; tournament-wide otherwise
          const evKeys = (ev) => {
            const c = ev && t.event_config?.[ev];
            const lvs = c ? ((c.levels || []).includes("All") ? null : c.levels) : (t.levels || []);
            const abs = c ? (c.age_brackets || []) : (t.age_brackets || []);
            if (!lvs) {
              const present = roster ? Object.keys(roster).filter((k) => (roster[k] || []).some(inEv(ev))) : [];
              return present.length ? present : (abs.length ? abs.map((ab) => `All ${ab}`) : ["All"]);
            }
            return lvs.flatMap((lv) => (abs.length ? abs.map((ab) => `${lv} ${ab}`) : [lv]));
          };
          const groups = t.event_types?.length ? t.event_types : [null];
          const sel = openLevel ? { ev: openLevel.slice(0, openLevel.indexOf("|")), lv: openLevel.slice(openLevel.indexOf("|") + 1) } : null;
          const selRows = sel && roster ? (roster[sel.lv] || []).filter((e) => !sel.ev || !e.event_type || e.event_type === sel.ev) : [];
          return (
          <div>
            <span className="text-zinc-600 uppercase tracking-widest text-[9px]">Levels</span>
            <div className="mt-1 space-y-1.5" data-testid={`level-groups-${t.id}`}>
              {groups.map((ev) => {
                const evCount = t.entry_count === 0 ? 0
                  : roster ? Object.values(roster).flat().filter((e) => !ev || !e.event_type || e.event_type === ev).reduce((n, e) => n + (e.players?.length || 1), 0) : null;
                return (
                <div key={ev || "all"}>
                  {ev && (
                    <button type="button" data-testid={`event-group-${t.id}-${ev}`} onClick={() => toggleGroup(ev)}
                      className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest mb-1 transition-colors ${openGroups[ev] ? "text-pb-red" : "text-zinc-300 hover:text-pb-red"}`}>
                      <ChevronRight className={`w-3 h-3 transition-transform ${openGroups[ev] ? "rotate-90" : ""}`} />
                      {ev}
                      {evCount !== null && <span data-testid={`event-count-${t.id}-${ev}`} className={`normal-case tracking-normal font-semibold ${evCount > 0 ? "text-pb-cyan" : "text-zinc-600"}`}>({evCount})</span>}
                    </button>
                  )}
                  {(!ev || openGroups[ev]) && (() => {
                  const levelKeys = evKeys(ev);
                  const orphanKeys = roster ? Object.keys(roster).filter((k) => !levelKeys.includes(k) && (roster[k] || []).some(inEv(ev))) : [];
                  return (
                  <div className={`flex flex-wrap items-center gap-1.5 ${ev ? "pl-4 mb-1" : ""}`}>
                    {levelKeys.map((lv) => {
                      const key = `${ev || ""}|${lv}`;
                      const cnt = t.entry_count === 0 ? 0 : roster ? (roster[lv] || []).filter((e) => !ev || !e.event_type || e.event_type === ev).reduce((n, e) => n + (e.players?.length || 1), 0) : null;
                      return (
                        <button key={key} type="button" data-testid={`level-chip-${t.id}-${ev ? `${ev}-` : ""}${lv}`}
                          onClick={() => toggleLevel(key)}
                          className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border transition-colors ${openLevel === key ? "bg-pb-red text-black border-pb-red" : "border-pb-border text-zinc-400 hover:border-pb-red hover:text-pb-red"}`}>
                          {lv}{cnt !== null && <span className={openLevel === key ? "" : cnt > 0 ? " text-pb-cyan" : " text-zinc-600"}> ({cnt})</span>}
                        </button>
                      );
                    })}
                    {orphanKeys.map((lv) => {
                      const key = `${ev || ""}|${lv}`;
                      const cnt = (roster[lv] || []).filter((e) => !ev || !e.event_type || e.event_type === ev).reduce((n, e) => n + (e.players?.length || 1), 0);
                      return (
                        <button key={key} type="button" data-testid={`level-chip-orphan-${t.id}-${lv}`}
                          title="These players signed up under a previous bracket" onClick={() => toggleLevel(key)}
                          className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border transition-colors ${openLevel === key ? "bg-amber-400 text-black border-amber-400" : "border-amber-500/50 text-amber-400 hover:bg-amber-500/10"}`}>
                          {lv} ({cnt})
                        </button>
                      );
                    })}
                  </div>
                  );
                  })()}
                </div>
                );
              })}
            </div>
            <div className="mt-1.5 text-[9px] text-zinc-600 leading-relaxed" data-testid={`division-breakdown-${t.id}`}>
              {t.event_config ? (t.event_types || []).map((ev) => {
                const c = t.event_config[ev] || {};
                const lv = (c.levels || []).includes("All") ? "Open skill" : (c.levels || []).filter((l) => DIVISION_RANGES[l]).map((l) => `${l} ${DIVISION_RANGES[l]}`).join(" · ");
                return <div key={ev}><span className="text-zinc-500">{ev}:</span> {lv}{c.age_brackets?.length ? ` · Ages ${c.age_brackets.join(", ")}` : ""}</div>;
              }) : ((t.levels || []).includes("All") || !(t.levels || []).length ? ALL_DIVISIONS : t.levels.filter((l) => DIVISION_RANGES[l]))
                .map((l) => `${l} ${DIVISION_RANGES[l]}`).join(" · ")}
            </div>
            {sel && (
              <div className="mt-2 border border-pb-border bg-pb-elevated/50 p-2.5" data-testid={`level-roster-${t.id}-${sel.lv}`}>
                <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">{sel.ev ? `${sel.ev} · ` : ""}{sel.lv}{divisionRange(sel.lv) ? ` (${divisionRange(sel.lv)})` : ""} — Signed Up</div>
                {!roster ? (
                  <div className="text-zinc-500 text-xs">Loading…</div>
                ) : selRows.length === 0 ? (
                  <div className="text-zinc-500 text-xs" data-testid={`level-roster-empty-${t.id}`}>No one signed up at this level yet — be the first!</div>
                ) : (
                  <div className="space-y-1">
                    {selRows.map((e) => (
                      <div key={e.id} className="flex items-center gap-2 text-xs text-zinc-300" data-testid={`level-roster-row-${e.id}`}>
                        <span className="w-1 h-1 bg-pb-red rounded-full shrink-0" />
                        <span>
                          {(e.players?.length ? e.players : [{ user_id: e.user_id, name: e.display_name }]).map((p, i) => (
                            <span key={i}>{i > 0 && <span className="text-zinc-600"> / </span>}<PlayerName userId={p.user_id} name={p.name} /></span>
                          ))}
                        </span>
                        {e.event_type && <span className="text-zinc-600 text-[10px]">{e.event_type}</span>}
                        {e.pending && <span className="text-amber-400 text-[9px] uppercase tracking-widest">pending</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })()}
        <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-pb-red" /> {t.date}{t.end_date ? ` – ${t.end_date}` : ""}{t.start_time ? ` · ${t.start_time}${t.end_time ? `–${t.end_time}` : ""}` : ""}</div>
        <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-pb-red" /> {t.location}{t.distance_miles != null && <span className="text-pb-cyan font-bold text-xs" data-testid={`distance-${t.id}`}>· {t.distance_miles} mi away</span>}</div>
        <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-pb-red" /> {t.entry_count}/{t.capacity} entries · <span data-testid={`card-price-${t.id}`}>{priceLabel(t)}</span></div>
        {t.early_bird_active && t.status === "open" && (
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-emerald-300" data-testid={`early-bird-${t.id}`}>
            🐦 Early Bird ${t.early_bird_fee} <span className="text-zinc-600 line-through normal-case">${t.entry_fee}</span>
            {t.early_bird_expires_at && <span className="text-zinc-500 normal-case tracking-normal">· until {new Date(t.early_bird_expires_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>}
          </div>
        )}
        {(t.sponsors || []).length > 0 && <SponsorStrip sponsors={t.sponsors} tid={t.id} />}
        {t.vip_addon_fee != null && t.status === "open" && (
          <div className="text-[10px] uppercase tracking-wider font-bold text-yellow-300" data-testid={`vip-addon-${t.id}`}>★ VIP Section add-on · ${t.vip_addon_fee}{t.price_display === "coin" && t.vip_addon_fee_coins ? ` / 🪙 ${t.vip_addon_fee_coins} BLC` : ""}</div>
        )}
        <div className="text-zinc-600">Director: {t.director_name}</div>
        {t.pbr_level && (
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[#BEF264]" data-testid={`pbr-chip-${t.id}`}>
            <Flag className="w-3 h-3" /> PBR Sanctioned · {t.pbr_level} · up to {t.pbr_max} pts
          </div>
        )}
        {t.pbr_level && ELIG_GATES[t.pbr_level] && (
          <div className="text-[9px] uppercase tracking-widest text-amber-400 font-bold" data-testid={`elig-gate-${t.id}`}>
            🔒 Entry Gate: {ELIG_GATES[t.pbr_level]}
          </div>
        )}
      </div>
      {sched.length > 0 && (
        <div className="mt-3">
          <button type="button" data-testid={`schedule-toggle-${t.id}`} aria-expanded={showSched}
            onClick={() => setShowSched((v) => !v)}
            className={`w-full flex items-center justify-between gap-2 border px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors ${showSched ? "border-pb-cyan/60 text-pb-cyan bg-pb-cyan/5" : "border-pb-border text-zinc-400 hover:border-pb-cyan/50 hover:text-pb-cyan"}`}>
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Day-by-Day Schedule</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSched ? "rotate-180" : ""}`} />
          </button>
          {showSched && (
            <div className="border border-t-0 border-pb-cyan/30 bg-pb-elevated/40 p-2.5 space-y-1.5 max-h-52 overflow-y-auto" data-testid={`schedule-panel-${t.id}`}>
              {schedDays.map((day) => (
                <div key={day}>
                  <div className="text-[9px] uppercase tracking-[0.25em] text-yellow-400 font-bold mb-1" data-testid={`card-schedule-day-${t.id}-${day}`}>
                    Day {day}{dayDate(day) ? ` — ${dayDate(day)}` : ""}
                  </div>
                  {sched.filter((s) => (s.day || 1) === day).map((s, i) => (
                    <div key={`${s.label}-${i}`} className="flex items-center gap-2 text-[11px] mb-1" data-testid={`card-schedule-row-${t.id}-${day}-${i}`}>
                      <span className="font-mono text-pb-cyan shrink-0">{s.window}</span>
                      <span className="text-zinc-300 truncate">{s.label}</span>
                      {s.phase === "Mixed" && <span className="text-[8px] uppercase tracking-widest text-amber-400 border border-amber-500/40 px-1 shrink-0">Mixed</span>}
                    </div>
                  ))}
                </div>
              ))}
              <div className="text-[9px] text-zinc-600 leading-relaxed pt-0.5">Windows are the director's plan — actual match times can shift on game day.</div>
            </div>
          )}
        </div>
      )}
      {t.status === "complete" && t.pbr_level && (
        <div className="mt-3">
          <button type="button" data-testid={`pbr-results-toggle-${t.id}`} aria-expanded={showResults} onClick={toggleResults}
            className={`w-full flex items-center justify-between gap-2 border px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors ${showResults ? "border-[#BEF264]/60 text-[#BEF264] bg-[#BEF264]/5" : "border-pb-border text-zinc-400 hover:border-[#BEF264]/50 hover:text-[#BEF264]"}`}>
            <span className="flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Results & PBR Points</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showResults ? "rotate-180" : ""}`} />
          </button>
          {showResults && (
            <div className="border border-t-0 border-[#BEF264]/30 bg-pb-elevated/40 p-2.5 max-h-52 overflow-y-auto" data-testid={`pbr-results-panel-${t.id}`}>
              {results === null && <Loader2 className="w-4 h-4 animate-spin text-[#BEF264]" />}
              {results?.length === 0 && <div className="text-[10px] text-zinc-600">No PBR results recorded for this event.</div>}
              {results?.length > 0 && results.map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-[11px] mb-1" data-testid={`pbr-result-row-${t.id}-${r.finish}`}>
                  <span className="shrink-0 w-8 text-zinc-400">{r.finish === 1 ? "🥇" : r.finish === 2 ? "🥈" : r.finish === 3 ? "🥉" : `#${r.finish}`}</span>
                  <span className="text-zinc-200 truncate">{r.player_name}</span>
                  <span className="text-zinc-600 shrink-0">{r.wins}–{r.losses}</span>
                  {r.rank_before && r.rank_after && r.rank_before !== r.rank_after && (
                    <span className={`shrink-0 text-[9px] font-bold ${r.rank_before > r.rank_after ? "text-green-400" : "text-red-400"}`}>#{r.rank_before}→#{r.rank_after}</span>
                  )}
                  <span className="ml-auto shrink-0 font-display font-black text-[#BEF264]">+{r.points}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2 mt-4">
        {canSeeBracket && (
          <button data-testid={`view-tournament-${t.id}`} onClick={onView}
            className="flex-1 flex items-center justify-center gap-1 border border-pb-border text-zinc-300 font-bold uppercase tracking-widest text-xs py-2.5 hover:border-pb-cyan hover:text-pb-cyan transition-colors">
            Bracket <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
        {onRegister && t.status === "open" && (!t.reg_state || t.reg_state === "open") && !t.my_entry && !t.my_waitlist && (
          spotsLeft > 0 ? (
            <button data-testid={`register-tournament-${t.id}`} onClick={onRegister}
              className="tn-btn tn-reg flex-1 h-[46px] active:scale-95 transition-transform">
              <span className="tn-inner text-[10px] tracking-[0.22em]">Register</span>
            </button>
          ) : (
            <button data-testid={`waitlist-tournament-${t.id}`} onClick={onRegister}
              className="tn-btn tn-wait flex-1 h-[46px] active:scale-95 transition-transform">
              <span className="tn-inner text-[9px] tracking-[0.16em]">Full — Join Waiting List</span>
            </button>
          )
        )}
        {onRegister && t.status === "open" && t.reg_state === "opening_soon" && !t.my_entry && !t.my_waitlist && (
          <div className="flex-1 flex items-center justify-center text-[10px] uppercase tracking-widest text-yellow-400 font-bold border border-yellow-500/50 bg-yellow-500/5 h-[46px]" data-testid={`reg-soon-btn-${t.id}`}>
            Registration Opens Soon 🔓
          </div>
        )}
        {t.my_waitlist && !t.my_entry && <div className="flex-1 flex items-center justify-center text-xs uppercase tracking-widest text-amber-400 font-bold border border-amber-500/40 bg-amber-500/5" data-testid="my-waitlist-badge">On Waiting List ⏳</div>}
        {t.my_entry && (() => {
          const remaining = (t.event_types || []).filter((ev) => !(t.my_events || []).includes(ev) && eventFitsGender(ev, user?.gender));
          return (
            <div className="flex-1 flex flex-col gap-1.5">
              <div className="flex items-center justify-center text-xs uppercase tracking-widest text-pb-red font-bold border border-pb-red/40 bg-pb-red/5 py-2" data-testid="my-entry-badge">
                Entered ✓{(t.my_events || []).length > 1 ? ` · ${t.my_events.length} events` : ""}
              </div>
              {onRegister && t.status === "open" && (!t.reg_state || t.reg_state === "open") && remaining.length > 0 && (
                <button data-testid={`add-event-${t.id}`} onClick={onRegister}
                  className="flex items-center justify-center gap-1 border border-pb-cyan/50 text-pb-cyan font-bold uppercase tracking-widest text-[10px] py-2 hover:bg-pb-cyan/10 transition-colors">
                  + Add Another Event ({remaining.join(" · ")})
                </button>
              )}
              <EditMyReg t={t} onChanged={onChanged} />
            </div>
          );
        })()}
      </div>
      {preview && (
        <Modal onClose={() => setPreview(false)} testid={`image-preview-${t.id}`} title={t.name} wide>
          {vidSrc ? (
            <video src={vidSrc} controls autoPlay playsInline loop className="w-full max-h-[60vh] bg-black border border-pb-border mb-4" data-testid={`video-preview-full-${t.id}`} />
          ) : (
            <img src={imgSrc} alt={t.name} className="w-full max-h-[60vh] object-contain border border-pb-border mb-4" data-testid={`image-preview-full-${t.id}`} />
          )}
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
            {t.date}{t.end_date ? ` – ${t.end_date}` : ""} · {t.location} · Director: {t.director_name}
          </div>
          {t.description ? (
            <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line" data-testid={`image-preview-description-${t.id}`}>{t.description}</p>
          ) : (
            <p className="text-sm text-zinc-600 italic" data-testid={`image-preview-no-description-${t.id}`}>The director hasn't added a description for this event yet.</p>
          )}
        </Modal>
      )}
    </div>
  );
}

function TdApplyCard() {
  const { user } = useAuth();
  const [form, setForm] = useState({ phone: "", experience: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user?.phone) setForm((f) => (f.phone ? f : { ...f, phone: user.phone }));
  }, [user]);

  const submit = async () => {
    if (!form.phone.trim()) return toast.error("Phone number is required");
    setBusy(true);
    try {
      await api.post("/td/apply", form);
      setDone(true);
      toast.success("Application submitted — we'll reach out!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const input = "w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red text-sm";
  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start mt-4" data-testid="td-apply-card">
      <div className="bg-gradient-to-br from-pb-red/10 to-transparent border border-pb-red/30 p-6 lg:p-8">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardList className="w-6 h-6 text-pb-red" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-pb-red font-bold">Run Tournaments</span>
        </div>
        <h3 className="font-display font-black uppercase text-3xl leading-none mb-3">Become a<br />Tournament Director</h3>
        <p className="text-sm text-zinc-400 leading-relaxed">Apply to run tournaments on the Bullpen platform — design brackets (single elim, double elim, round robin, pool play), manage scores live, and crown champions. We review every application personally.</p>
      </div>
      <div className="bg-pb-surface border border-pb-border p-6">
        {done ? (
          <div className="text-center py-6" data-testid="td-apply-success">
            <Trophy className="w-10 h-10 text-pb-red mx-auto mb-3" />
            <div className="font-display font-black uppercase text-xl mb-1">Application Received</div>
            <p className="text-sm text-zinc-400">We'll review it and reach out at {form.phone}.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div><label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Phone *</label>
              <input data-testid="td-apply-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="941-555-0000" className={input} /></div>
            <div><label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Tournament Experience</label>
              <input data-testid="td-apply-experience" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} placeholder="e.g. Ran 5 local round robins" className={input} /></div>
            <div><label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Anything else?</label>
              <textarea data-testid="td-apply-message" rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${input} resize-none`} /></div>
            <button data-testid="td-apply-submit" onClick={submit} disabled={busy}
              className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
              <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Apply to Direct"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Modal({ children, onClose, title, testid, wide }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return createPortal(
    <div className="fixed inset-0 z-[95] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-pb-surface border border-pb-red w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[88vh] flex flex-col animate-fade-up`} onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between p-5 border-b border-pb-border shrink-0">
          <h3 className="font-display font-black text-lg uppercase">{title}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid={`${testid}-close`}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto overscroll-contain">{children}</div>
      </div>
    </div>,
    document.body
  );
}
